import request from 'graphql-request';

import * as constants from '../constants';
import { AuthorizationError } from '@repo/ui/state';
import { QueryStateManagement } from '@/state';
import { ConfigService, extractErrorFromResponse } from '../config';
import {
  BulkChangeStageMutation,
  ContactInfo,
  ExportToCopilotResponse,
  SubmissionQueryParams,
  SubmissionsResponse,
} from '@repo/api/open-calls/graphql';
import {
  bulkUpdateStageQuery,
  candidateExportToFileQuery,
  exportFinalistsToCopilotQuery,
  submissionsQuery,
} from './queries.graphql';

type SubmissionsResponseWrapper = {
  submissions: SubmissionsResponse;
};

export class SubmissionsService {
  static async querySubmissionsBy(
    params: SubmissionQueryParams,
  ): Promise<SubmissionsResponse & AuthorizationError> {
    const submissionFilters = structuredClone(params.filters);
    const {
      sort,
      filters: { openCallId, stageId },
      pagination,
      excludeIds,
    } = params;

    if (!openCallId || !stageId) {
      throw new Error(
        '`openCallId` and `stageId` candidate filters are required',
      );
    }

    const queryParams = {
      filters: submissionFilters,
      pagination,
      sort,
      excludeIds,
    };

    try {
      const {
        submissions: { data, count, summary, _ids },
      } = await QueryStateManagement.client.fetchQuery({
        queryKey: ['candidates', params],
        queryFn: () =>
          request<SubmissionsResponseWrapper>(
            `${ConfigService.BACKEND_GRAPHQL_ENDPOINT}${window.location.search}`,
            submissionsQuery,
            { params: queryParams },
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });

      return {
        _ids,
        count,
        data,
        summary,
      } as SubmissionsResponse & AuthorizationError;
    } catch (error) {
      const err = extractErrorFromResponse(error);
      console.error('Error querying submissions', err);
      throw err;
    }
  }

  static async moveToStage(
    candidateIds: string[],
    targetStageId: string,
    userId: string,
  ) {
    try {
      const res = await QueryStateManagement.client.fetchQuery({
        queryKey: ['candidates', candidateIds],
        queryFn: () =>
          request<BulkChangeStageMutation>(
            ConfigService.BACKEND_GRAPHQL_ENDPOINT,
            bulkUpdateStageQuery,
            { candidateIds, userId, stageId: targetStageId },
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });
      return res.bulkChangeStage.count;
    } catch (error) {
      const err = extractErrorFromResponse(error);
      console.error('Error moving candidates to stage', err);
      throw err;
    }
  }

  static async contactExport(candidateIds: string[]) {
    try {
      const res: { contactExport: ContactInfo[] } =
        await QueryStateManagement.client.fetchQuery({
          queryKey: ['candidateExport', candidateIds],
          queryFn: () =>
            request<void>(
              ConfigService.BACKEND_GRAPHQL_ENDPOINT,
              candidateExportToFileQuery,
              { candidateIds },
              {
                Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
              },
            ),
          gcTime: constants.QUERY_CACHE_TIME,
        });
      return res.contactExport;
    } catch (error) {
      const err = extractErrorFromResponse(error);
      console.error('Error exporting contacts', err);
      throw err;
    }
  }

  static async exportFinalistsToCopilot(
    candidateIds: string[],
    openCall: { id: string; name: string },
  ) {
    try {
      const res: { exportFinalistsToCopilot: ExportToCopilotResponse } =
        await QueryStateManagement.client.fetchQuery({
          queryKey: ['export-finalists-to-copilot', openCall, candidateIds],
          queryFn: () =>
            request<void>(
              ConfigService.BACKEND_GRAPHQL_ENDPOINT,
              exportFinalistsToCopilotQuery,
              { candidateIds, openCall },
              {
                Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
              },
            ),
          gcTime: constants.QUERY_CACHE_TIME,
        });

      return res.exportFinalistsToCopilot;
    } catch (error) {
      const err = extractErrorFromResponse(error);
      console.error('Error exporting candidates to Copilot', err);
      throw err;
    }
  }
}
